import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordenesClientesAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Building2,
    Car,
    Package,
    ClipboardList,
    Phone,
    Mail,
    MapPin,
    User,
    FileText,
    CheckCircle,
    Clock,
    DollarSign,
    Wrench,
} from 'lucide-react';

const TIPOS_DISPOSITIVO = {
    gps: { nombre: 'GPS', color: 'bg-blue-100 text-blue-700' },
    sensor_fatiga: { nombre: 'Sensor Fatiga', color: 'bg-purple-100 text-purple-700' },
    camara_frontal: { nombre: 'Cámara Frontal', color: 'bg-green-100 text-green-700' },
    camara_interior: { nombre: 'Cámara Interior', color: 'bg-teal-100 text-teal-700' },
    sensor_combustible: { nombre: 'Sensor Combustible', color: 'bg-orange-100 text-orange-700' },
    boton_panico: { nombre: 'Botón Pánico', color: 'bg-red-100 text-red-700' },
};

const ESTADO_ORDEN = {
    pendiente: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    en_proceso: { color: 'bg-blue-100 text-blue-700', icon: Wrench },
    realizada: { color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
    facturada: { color: 'bg-green-100 text-green-700', icon: DollarSign },
};

export default function ClienteDetallePage() {
    const { clienteId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [cliente, setCliente] = useState(null);
    const [activeTab, setActiveTab] = useState('info');

    useEffect(() => {
        loadCliente();
    }, [clienteId]);

    const loadCliente = async () => {
        try {
            setLoading(true);
            const res = await ordenesClientesAPI.getById(clienteId);
            setCliente(res.data);
        } catch (err) {
            toast.error('Error al cargar cliente');
            navigate('/ordenes/clientes');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!cliente) {
        return null;
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/clientes')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Building2 className="h-6 w-6" />
                            {cliente.razon_social}
                        </h1>
                        <p className="text-muted-foreground font-mono">{cliente.rut}</p>
                    </div>
                </div>
                <Button onClick={() => navigate(`/ordenes/nueva?cliente_id=${cliente.id}`)}>
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Nueva Orden
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <Car className="h-8 w-8 text-blue-500" />
                            <div>
                                <p className="text-2xl font-bold">{cliente.total_vehiculos}</p>
                                <p className="text-xs text-muted-foreground">Vehículos</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <Package className="h-8 w-8 text-green-500" />
                            <div>
                                <p className="text-2xl font-bold">{cliente.total_dispositivos}</p>
                                <p className="text-xs text-muted-foreground">Dispositivos</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="col-span-2">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <ClipboardList className="h-8 w-8 text-purple-500" />
                            <div>
                                <p className="text-2xl font-bold">{cliente.ordenes_historial?.length || 0}</p>
                                <p className="text-xs text-muted-foreground">Órdenes de Servicio</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Resumen de Dispositivos */}
            {Object.keys(cliente.resumen_dispositivos || {}).length > 0 && (
                <Card className="border-2 border-primary/20 bg-primary/5">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Package className="h-5 w-5" />
                            Resumen de Dispositivos Instalados
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(cliente.resumen_dispositivos).map(([tipo, info]) => {
                                const config = TIPOS_DISPOSITIVO[tipo] || { nombre: tipo, color: 'bg-gray-100 text-gray-700' };
                                return (
                                    <div key={tipo} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                                        <div className="flex items-center gap-3">
                                            <Badge className={config.color}>{info.cantidad}</Badge>
                                            <span className="font-medium">{config.nombre}</span>
                                        </div>
                                        <span className="text-sm text-muted-foreground">
                                            ({info.patentes.join(', ')})
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="info">Información</TabsTrigger>
                    <TabsTrigger value="vehiculos">Vehículos ({cliente.vehiculos?.length || 0})</TabsTrigger>
                    <TabsTrigger value="ordenes">Historial ({cliente.ordenes_historial?.length || 0})</TabsTrigger>
                </TabsList>

                {/* Info Tab */}
                <TabsContent value="info">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {cliente.nombre_fantasia && (
                                    <div className="flex items-start gap-3">
                                        <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Nombre Fantasía</p>
                                            <p className="font-medium">{cliente.nombre_fantasia}</p>
                                        </div>
                                    </div>
                                )}
                                {cliente.giro && (
                                    <div className="flex items-start gap-3">
                                        <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Giro</p>
                                            <p className="font-medium">{cliente.giro}</p>
                                        </div>
                                    </div>
                                )}
                                {cliente.direccion && (
                                    <div className="flex items-start gap-3">
                                        <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Dirección</p>
                                            <p className="font-medium">{cliente.direccion}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {[cliente.comuna, cliente.ciudad].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {cliente.telefono && (
                                    <div className="flex items-start gap-3">
                                        <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Teléfono</p>
                                            <p className="font-medium">{cliente.telefono}</p>
                                        </div>
                                    </div>
                                )}
                                {cliente.email && (
                                    <div className="flex items-start gap-3">
                                        <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Email</p>
                                            <p className="font-medium">{cliente.email}</p>
                                        </div>
                                    </div>
                                )}
                                {cliente.contacto_principal && (
                                    <div className="flex items-start gap-3">
                                        <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Contacto Principal</p>
                                            <p className="font-medium">{cliente.contacto_principal}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Vehículos Tab */}
                <TabsContent value="vehiculos">
                    <Card>
                        <CardContent className="p-0 overflow-x-auto">
                            {cliente.vehiculos?.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Car className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                    <p>No hay vehículos registrados</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Patente</TableHead>
                                            <TableHead>Vehículo</TableHead>
                                            <TableHead>Año</TableHead>
                                            <TableHead>Dispositivos</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {cliente.vehiculos?.map((vehiculo) => (
                                            <TableRow key={vehiculo.id}>
                                                <TableCell className="font-mono font-bold">{vehiculo.patente}</TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium">{vehiculo.marca} {vehiculo.modelo}</p>
                                                        <p className="text-xs text-muted-foreground">{vehiculo.tipo_vehiculo}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{vehiculo.anio || '-'}</TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {vehiculo.dispositivos?.map((d, i) => {
                                                            const config = TIPOS_DISPOSITIVO[d.tipo_dispositivo] || { nombre: d.tipo_dispositivo, color: 'bg-gray-100' };
                                                            return (
                                                                <Badge key={i} variant="secondary" className={config.color}>
                                                                    {config.nombre}
                                                                </Badge>
                                                            );
                                                        })}
                                                        {(!vehiculo.dispositivos || vehiculo.dispositivos.length === 0) && (
                                                            <span className="text-muted-foreground text-sm">Sin dispositivos</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => navigate(`/ordenes/vehiculos/${vehiculo.id}`)}
                                                    >
                                                        Ver Historial
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Historial Tab */}
                <TabsContent value="ordenes">
                    <Card>
                        <CardContent className="p-0 overflow-x-auto">
                            {cliente.ordenes_historial?.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                    <p>No hay órdenes de servicio</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>N° Orden</TableHead>
                                            <TableHead>Tipo</TableHead>
                                            <TableHead>Vehículo</TableHead>
                                            <TableHead>Estado</TableHead>
                                            <TableHead>Fecha</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {cliente.ordenes_historial?.map((orden) => {
                                            const estadoConfig = ESTADO_ORDEN[orden.estado] || ESTADO_ORDEN.pendiente;
                                            const Icon = estadoConfig.icon;
                                            return (
                                                <TableRow 
                                                    key={orden.id} 
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => navigate(`/ordenes/ordenes/${orden.id}`)}
                                                >
                                                    <TableCell className="font-mono font-bold">{orden.numero_orden}</TableCell>
                                                    <TableCell>{orden.tipo_servicio?.replace(/_/g, ' ')}</TableCell>
                                                    <TableCell>{orden.vehiculo?.patente || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge className={estadoConfig.color}>
                                                            <Icon className="h-3 w-3 mr-1" />
                                                            {orden.estado}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(orden.created_at).toLocaleDateString('es-CL')}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
